import React from 'react'

import cx from 'classnames'
import debounce from 'lodash/debounce'

import { Div, IconButton, Input, Span } from '@driftt/tide-core'
import { Select } from '@driftt/tide-selects'

import {
  DatePicker,
  DateRangePicker,
  getDefaultDate,
  getDefaultRange} from 'components/DateRangePicker'
import {
  FilterConfigAndAvailableValues,
  FilterOperator,
  FilterPlurality,
  FilterValues,
  FilterValueType,
  operatorToString} from 'context/filtersContext'
import useFilterAttributeOptions from 'hooks/useFilterAttributeOptions'

import styles from '../styles.module.css'

interface FilterRowProps {
  filter: FilterConfigAndAvailableValues
  filters: FilterValues[]
  filterConfiguration: FilterConfigAndAvailableValues[]
  comparator: FilterOperator
  selectedValues?: any
  onChangeFilterType: (value: any) => void
  onChangeComparator: (value: any) => void
  onChangeValue: (value: any) => void
  onRemoveFilter: () => void
  calendarOpenDirection?: 'up' | 'down'
  showFormValidation?: boolean
}

const selectStyles = {
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 40
  })
}

const invalidFilterMessage = 'Enter a value to apply your filters'

const FilterRow: React.FC<FilterRowProps> = ({
  filter,
  filterConfiguration,
  filters,
  comparator,
  selectedValues,
  onChangeFilterType,
  onChangeComparator,
  onChangeValue,
  onRemoveFilter,
  calendarOpenDirection,
  showFormValidation
}) => {
  const filterAttributeOptions = useFilterAttributeOptions(filter)

  const isAlreadySelected = (filter: FilterConfigAndAvailableValues) =>
    !!filters.find(
      (f: FilterValues) =>
        f.filterType === filter.filterType && f.filterSubtype === filter.filterSubtype
    )

  const driftAttrOptions = filterConfiguration
    .filter(f => f.filterSubtype === undefined)
    .map(f => ({
      value: {
        filterType: f.filterType
      },
      label: f.filterDisplayName,
      isDisabled: isAlreadySelected(f)
    }))

  const customAttrOptions = filterConfiguration
    .filter(f => f.filterSubtype !== undefined)
    .map(f => ({
      value: {
        valueType: f.valueType,
        filterType: f.filterType,
        filterSubtype: f.filterSubtype,
        attributeType: f.attributeType
      },
      label: f.filterDisplayName,
      isDisabled: isAlreadySelected(f)
    }))

  const filterTypeOptions =
    customAttrOptions.length > 0
      ? [
          {
            label: 'Drift Attributes',
            options: driftAttrOptions
          },
          {
            label: 'Custom Attributes',
            options: customAttrOptions
          }
        ]
      : driftAttrOptions

  const onChangeValueDebounced = debounce(onChangeValue, 750)
  const bodyEl = document.querySelector('body')
  const isFilterValueInvalid = showFormValidation && !filter.getIsFilterValid(selectedValues)

  return (
    <div className={styles.filterContainer} style={filter.gridTemplateStyle}>
      <Select
        className={cx({
          [styles.cursorDisabled]: filterTypeOptions.length === 0,
          filterSelect: true
        })}
        clearable={false}
        creatable={false}
        noResultsText={false}
        searchable={false}
        size={'small'}
        selectStyles={selectStyles}
        value={{ value: filter, label: filter.filterDisplayName }}
        onChange={onChangeFilterType}
        options={filterTypeOptions}
        isDisabled={filters.length === filterConfiguration.length}
        menuPortalTarget={bodyEl}
        menuPlacement="auto"
        minMenuHeight={245}
        maxMenuHeight={245}
      />
      <Select
        className={cx({
          [styles.cursorDisabled]: filter.operators?.length <= 1,
          filterSelect: true
        })}
        clearable={false}
        creatable={false}
        size={'small'}
        noResultsText={false}
        searchable={false}
        selectStyles={selectStyles}
        value={{ value: comparator, label: operatorToString(comparator) }}
        onChange={onChangeComparator}
        options={
          filter.operators?.length > 1
            ? filter.operators.map(operator => ({
                value: operator,
                label: operatorToString(operator)
              }))
            : []
        }
        isDisabled={filter.operators.length < 2}
        menuPortalTarget={bodyEl}
        menuPlacement="auto"
        minMenuHeight={245}
        maxMenuHeight={245}
      />
      {filter.valueType === FilterValueType.STRING && (
        <Div>
          <Select
            className={cx('filterSelect', { [styles.errorSelect]: isFilterValueInvalid })}
            isMulti={filter.plurality === FilterPlurality.MULTIPLE}
            clearable={false}
            creatable={filter.creatable}
            size={'small'}
            selectStyles={selectStyles}
            value={selectedValues}
            onChange={onChangeValue}
            options={filterAttributeOptions}
            menuPortalTarget={bodyEl}
            menuPlacement="auto"
            minMenuHeight={245}
            maxMenuHeight={245}
            required
          />
          {isFilterValueInvalid && <Span className={styles.redText}>{invalidFilterMessage}</Span>}
        </Div>
      )}
      {filter.valueType === FilterValueType.NUMBER && (
        <Div>
          <Input
            min={0}
            className={styles.numberInput}
            type="number"
            defaultValue={selectedValues !== undefined ? selectedValues : ''}
            onChange={(e: any) =>
              onChangeValueDebounced(e.target.value ? Number(e.target.value) : undefined)
            }
          />
          {isFilterValueInvalid && <Span className={styles.redText}>{invalidFilterMessage}</Span>}
        </Div>
      )}
      {filter.valueType === FilterValueType.DATE && comparator === FilterOperator.BETWEEN && (
        <DateRangePicker
          //@ts-ignore
          onDateRangeChange={onChangeValue}
          dateRange={selectedValues || getDefaultRange()}
          openDirection={calendarOpenDirection}
        />
      )}

      {filter.valueType === FilterValueType.DATE && comparator === FilterOperator.EQUALS && (
        <DatePicker
          //@ts-ignore
          onChangeDate={onChangeValue}
          date={selectedValues || getDefaultDate()}
          openDirection={calendarOpenDirection}
        />
      )}
      <IconButton
        onClick={onRemoveFilter}
        className={styles.closeMargin}
        icon="cancel-circle"
        height={18}
        width={18}
      />
    </div>
  )
}

export default FilterRow
